import React, { useEffect, useState } from "react";
import {
  ShoppingBag, DollarSign, Receipt, Boxes, Store, PackageX, Clock3, Trash2,
  AlertTriangle, TrendingUp, Percent,
} from "lucide-react";
import api from "@/lib/api";
import { money, num, Spinner, StatusPill, PageHeader, BRAND_DOT } from "@/components/shared";
import { Progress } from "@/components/ui/progress";

const KPIS = [
  { key: "orders_today", title: "Orders Today", icon: ShoppingBag, fmt: num, tint: "text-sky-400", testid: "kpi-orders-today" },
  { key: "sales_today", title: "Sales Today", icon: DollarSign, fmt: money, tint: "text-emerald-400", testid: "kpi-sales-today" },
  { key: "client_charges_today", title: "Client Charges Today", icon: Receipt, fmt: money, tint: "text-amber-400", testid: "kpi-charges-today" },
  { key: "inventory_value", title: "Inventory Value", icon: Boxes, fmt: money, tint: "text-indigo-400", testid: "kpi-inventory-val" },
  { key: "active_brands", title: "Active Brands", icon: Store, fmt: num, tint: "text-violet-400", testid: "kpi-active-brands" },
  { key: "low_stock_items", title: "Low / Reorder", icon: AlertTriangle, fmt: num, tint: "text-orange-400", testid: "kpi-low-stock" },
  { key: "expiring_items", title: "Expiring Soon", icon: Clock3, fmt: num, tint: "text-rose-400", testid: "kpi-expiring" },
  { key: "wastage_today", title: "Wastage Today", icon: Trash2, fmt: money, tint: "text-rose-300", testid: "kpi-wastage" },
  { key: "commission_today", title: "Commission Today", icon: Percent, fmt: money, tint: "text-orange-300", testid: "kpi-commission" },
  { key: "gross_profit_today", title: "Gross Profit Today", icon: TrendingUp, fmt: money, tint: "text-emerald-300", testid: "kpi-profit" },
];

function capColor(pct) {
  if (pct >= 100) return "text-rose-400";
  if (pct >= 90) return "text-orange-400";
  if (pct >= 80) return "text-amber-400";
  return "text-emerald-400";
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
    api.get("/inventory/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  }, []);

  if (!data) return <Spinner label="Loading dashboard" />;

  return (
    <div>
      <PageHeader title="Operations Dashboard" subtitle={data.kitchen_name} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {KPIS.map((k) => (
          <div key={k.key} data-testid={k.testid} className="card-surface p-4 sm:p-5 hover:border-primary/40 transition-colors">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wider text-muted-foreground font-mono">{k.title}</span>
              <k.icon className={`h-4 w-4 ${k.tint}`} />
            </div>
            <p className="text-2xl sm:text-3xl font-extrabold font-mono text-white tracking-tight">{k.fmt(data.kpis[k.key])}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 card-surface p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-amber-400" />
            <h3 className="font-display font-semibold text-white">Brand Capacity & Performance</h3>
          </div>
          <div className="space-y-3" data-testid="brand-capacity-list">
            {data.brands.map((b) => (
              <div key={b.id} className="rounded-lg border border-border p-3.5 bg-secondary/30" data-testid={`brand-row-${b.id}`}>
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${BRAND_DOT[b.color] || "bg-slate-400"}`} />
                    <div className="min-w-0">
                      <p className="font-semibold text-white truncate">{b.name}</p>
                      <p className="text-[11px] text-muted-foreground font-mono">{b.op_start}–{b.op_end}</p>
                    </div>
                  </div>
                  <StatusPill status={b.status} testid={`brand-status-${b.id}`} />
                </div>
                <div className="flex items-center gap-4 text-xs mb-2 flex-wrap">
                  <span className="text-slate-300">Orders <b className="font-mono text-white">{b.orders_today}</b>/{b.daily_capacity}</span>
                  <span className="text-slate-300">Sales <b className="font-mono text-white">{money(b.sales)}</b></span>
                  <span className="text-slate-300">Consumption <b className="font-mono text-amber-300">{money(b.client_charge)}</b></span>
                  {b.commission > 0 && <span className="text-slate-300">Comm <b className="font-mono text-orange-300">{money(b.commission)}</b></span>}
                  <span className="text-slate-300">Profit <b className="font-mono text-emerald-300">{money(b.profit)}</b></span>
                  <span className={`font-mono font-bold ml-auto ${capColor(b.capacity_pct)}`}>{b.capacity_pct}%</span>
                </div>
                <Progress value={Math.min(b.capacity_pct, 100)} className="h-1.5" />
              </div>
            ))}
          </div>
        </div>

        <div className="card-surface p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-4 w-4 text-rose-400" />
            <h3 className="font-display font-semibold text-white">Alerts</h3>
          </div>
          {!alerts ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="space-y-4" data-testid="alerts-panel">
              <AlertGroup icon={PackageX} label="Out of Stock" items={alerts.out_of_stock} color="text-rose-400" render={(i) => i.name} />
              <AlertGroup icon={AlertTriangle} label="Low / Reorder" items={[...alerts.low_stock, ...alerts.reorder_required]} color="text-orange-400" render={(i) => `${i.name} · ${num(i.current_quantity)}${i.purchase_unit}`} />
              <AlertGroup icon={Clock3} label="Expiring Soon" items={alerts.expiring_soon} color="text-amber-400" render={(i) => `${i.name} · ${i.days_to_expiry}d`} />
              {alerts.out_of_stock.length + alerts.low_stock.length + alerts.reorder_required.length + alerts.expiring_soon.length === 0 && (
                <p className="text-sm text-emerald-400">All clear — no active alerts.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AlertGroup({ icon: Icon, label, items, color, render }) {
  const uniq = Array.from(new Map(items.map((i) => [i.id, i])).values());
  if (!uniq.length) return null;
  return (
    <div>
      <div className={`flex items-center gap-1.5 text-xs font-semibold ${color} mb-1.5`}>
        <Icon className="h-3.5 w-3.5" /> {label} ({uniq.length})
      </div>
      <ul className="space-y-1">
        {uniq.slice(0, 6).map((i) => (
          <li key={i.id} className="text-sm text-slate-300 pl-5">{render(i)}</li>
        ))}
      </ul>
    </div>
  );
}
